import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';

import Link from '@pcid/link';
import { AutoFormattedMessage } from '@pcid/string-utils';
import Layout, { SecondaryCTA } from '@pcid/components/layout';
import { handleHousekeeping } from '@pcid/utils/form-utils';
import oops from '@pcid/utils/oops';
import { localeCodes } from '@pcid/locale-utils';
import { getEmailStorage, getRelyingPartyStorage, getLoginCtxStorage } from '@pcid/storage-utils';
import { withLocale } from '@pcid/components/locale-provider';
import track from '@pcid/analytics/track';
import { withFeatureFlags } from '@pcid/components/config-context';
import withTarget from '@pcid/components/with-target';
import Sprite from '@pcid/sprite';
import constants from '../actions/track-analytics/constants';
import CreateAccountForm from '../components/forms/createAccountForm';
import processCreateAccount from '../actions/create-account';
import validateEmail from '../actions/validate-email';

const {
	linkName: { CREATE_ACCOUNT_SIGN_IN_LINK },
} = constants;

const CreateAccount = ({ locale, featureFlags: { sso, mandatory2fa }, target }) => {
	useEffect(() => {
		target.applyOffers();
	}, []);

	// check loginCtx. If not present, redirect to oops page
	// else the user will see an error after creating the account
	const relyingParty = getRelyingPartyStorage().get();
	const loginCtx = getLoginCtxStorage().getIn(relyingParty);
	if (!loginCtx) oops.noLoginInfo();

	const convergenceBanner = (
		<div className="create-account-form__convergence-box">
			<div className="banner create-account-form__convergence-banner">
				<div className="create-account-form__convergence-banner-logo-container">
					<Sprite
						group="banners"
						symbol={`pc-id_${locale}`}
						alt="PCid Logo"
						className="layout__pcid-logo-banner"
					/>
				</div>
				<div className="create-account-form__convergence-banner-description1-container">
					<AutoFormattedMessage
						id="form.createAccount.description1"
					/>
				</div>
				<div className="create-account-form__convergence-banner-description-container">
					<Sprite
						group="banners"
						symbol="check"
						alt="check"
						className="create-account-form__convergence-banner-description-check"
					/>
					<AutoFormattedMessage
						id="form.createAccount.description2"
					/>
				</div>
				<div className="create-account-form__convergence-banner-description-container">
					<Sprite
						group="banners"
						symbol="check"
						alt="check"
						className="create-account-form__convergence-banner-description-check"
					/>
					<AutoFormattedMessage
						id="form.createAccount.description3"
					/>
				</div>
				<div className="create-account-form__convergence-banner-description-container">
					<Sprite
						group="banners"
						symbol="check"
						alt="check"
						className="create-account-form__convergence-banner-description-check"
					/>
					<AutoFormattedMessage
						id="form.createAccount.description4"
					/>
				</div>
				<hr className="create-account-form__divider" />
				<Sprite
					group="banners"
					symbol={`banner-optimum_${locale}`}
					className="create-account-form__convergence-banner-svg"
				/>
				<Sprite
					group="banners"
					symbol={`banner-express_${locale}`}
					className="create-account-form__convergence-banner-svg"
				/>
				<Sprite
					group="banners"
					symbol={`banner-health_${locale}`}
					className="create-account-form__convergence-banner-svg login-form__last-svg"
				/>
				<div>
					<Sprite
						group="banners"
						symbol={`banner-shoppers_${locale}`}
						className="create-account-form__convergence-banner-svg"
					/>
					<Sprite
						group="banner"
						symbol={`banner-lifemark_${locale}`}
						className="create-account-form__convergence-banner-svg"
					/>
					<Sprite
						group="banners"
						symbol="banner-joe-fresh"
						className="create-account-form__convergence-banner-svg login-form__last-svg"
					/>
				</div>
			</div>
		</div>
	);

	return (
		<Layout
			title="page.title.createAccount"
			className="create-account"
			convergenceBanner={convergenceBanner}
			convergenceBannerPosition="left"
		>
			<Formik
				render={(props) => (
					<CreateAccountForm
						{...props}
						checkEmail={validateEmail}
						locale={locale}
						setEmail={(email) => { getEmailStorage().set(email); }}
						relyingParty={getRelyingPartyStorage().get()}
					/>
				)}
				onSubmit={handleHousekeeping(({ confirmPassword, ...data }) => (
					processCreateAccount({ ...data, sso, mandatory2fa, locale })
				))}
				initialValues={{
					firstName: '',
					lastName: '',
					email: '',
					newPassword: '',
					confirmPassword: '',
					casl: false,
					termsAndConditions: false,
				}}
				validateOnBlur={false}
				validateOnChange={false}
			/>
			<SecondaryCTA className="create-account-form__secondary-cta">
				<AutoFormattedMessage
					id="form.createAccount.backToSignIn"
					values={{
						signIn: (
							<Link
								to="/login?hidePageView=true"
								onClick={() => {
									track.customLink({ linkName: CREATE_ACCOUNT_SIGN_IN_LINK });
								}}
							>
								<AutoFormattedMessage id="form.common.signIn" />
							</Link>
						),
					}}
				/>
			</SecondaryCTA>
		</Layout>
	);
};

CreateAccount.propTypes = {
	checkEmail: PropTypes.func,
	createAccount: PropTypes.func,
	locale: PropTypes.string,
	featureFlags: PropTypes.object.isRequired,
	target: PropTypes.object.isRequired,
};

CreateAccount.defaultProps = {
	locale: localeCodes.EN,
};

export default withFeatureFlags(withLocale(withTarget(CreateAccount)));
